import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Pets } from '@mui/icons-material';
import apiService from '../services/api';

// Mirrors the backend's minimum in PasswordResetConfirmView.
const MIN_PASSWORD_LENGTH = 8;

/**
 * Step 2 of password reset — the page the emailed link actually opens.
 *
 * The backend has been sending users to {FRONTEND_URL}/reset-password?uid=..&token=..
 * with no such route defined, so every reset email landed on a blank page.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const uid = params.get('uid');
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const linkIsUsable = Boolean(uid && token);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await apiService.confirmPasswordReset(uid, token, password);
      setDone(true);
      // Give the confirmation a beat to be read before moving on.
      setTimeout(() => navigate('/auth'), 2500);
    } catch (err) {
      setError(err.message || 'This reset link is invalid or has expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Paper sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Pets sx={{ fontSize: 40, color: 'primary.main' }} aria-hidden="true" />
            <Typography variant="h5" component="h1" sx={{ mt: 1 }}>
              Choose a new password
            </Typography>
          </Box>

          {!linkIsUsable ? (
            <>
              <Alert severity="error" sx={{ mb: 3 }}>
                This reset link is incomplete. Please request a new one.
              </Alert>
              <Button
                component={RouterLink}
                to="/forgot-password"
                fullWidth
                variant="contained"
                sx={{ boxShadow: 'none', '&:active': { boxShadow: 'none', transform: 'none' }, }}
              >
                Request a new link
              </Button>
            </>
          ) : done ? (
            <Alert severity="success">
              Your password has been reset. Taking you to sign in&hellip;
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="New password"
                type="password"
                name="new-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
                sx={{ mb: 2 }}
              />

              <TextField
                label="Confirm new password"
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{ boxShadow: 'none', '&:active': { boxShadow: 'none', transform: 'none' }, }}
                disabled={submitting || !password || !confirmPassword}
              >
                {submitting ? <CircularProgress size={24} /> : 'Set new password'}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
